import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { ToastProvider } from './components/ui/Toast';
import './styles/globals.css';

// Global error handlers — surface (don't swallow) async errors that React's
// ErrorBoundary cannot catch (promise rejections, non-React event-handler throws).
// Logs only error name/message/stack — never request bodies, tokens, or other
// secret-bearing payloads.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason);
    console.error('[unhandledrejection]', message, reason instanceof Error ? reason.stack : undefined);
  });

  window.addEventListener('error', (event) => {
    // Resource load errors (img/script) surface as ErrorEvent with no .error.
    const detail = event.error instanceof Error
      ? `${event.error.name}: ${event.error.message}`
      : event.message;
    console.error('[window.onerror]', detail, {
      source: event.filename,
      line: event.lineno,
      col: event.colno,
    });
  });
}

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* BP-080: keep router updates out of startTransition (prop stabilized from unstable_useTransitions in react-router 7.14+) */}
      <BrowserRouter useTransitions={false}>
        <ToastProvider>
          <App />
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
